// 통합 아젠다 (일정 + 업무)
//
// 범위 단위 캐시를 둔다. 이 캐시가 세 가지 문제를 한 번에 없앤다.
//   1. Calendar.vue 가 월을 넘길 때마다 무조건 재조회하던 것
//   2. 예전 대시보드가 하루를 그리려고 ±30일(61일치)을 받아오던 것
//   3. TaskCalendar 가 Calendar 와 별개로 한 번 더 조회하던 것
//
// state 는 루트에 병합되므로 다른 조각과 이름이 겹치지 않게 agenda* 접두사를 쓴다.

import http from '@/utils/http'
import { normalizeAgendaList } from '@/utils/agendaItem'

const TTL_MS = 60 * 1000

function rangeKey(p) {
  return [
    p.from, p.to,
    p.scope || 'all',
    p.userId || '',
    p.departmentId || '',
    (p.kinds || []).join('|') || 'schedule,task',
    p.status || '',
    p.assigneeId || '',
    p.q || '',
    // 요청에 실리는 값은 전부 키에 들어가야 한다. 빠지면 eventType 만 다른 두 조회가
    // 같은 캐시 칸을 나눠 써서, 먼저 받은 쪽의 결과가 다른 쪽 화면에 그려진다.
    p.eventType || '',
    p.includeUnscheduled ? 'u' : ''
  ].join('~')
}

export const state = {
  // key -> { items, unscheduled, counts, truncated, fetchedAt }
  agendaRanges: {},
  agendaInflight: {},
  agendaLoading: false,
  // 무효화 세대. 요청이 나간 뒤 이 값이 바뀌었으면 그 응답은 이미 낡았다.
  agendaGen: 0
}

export const mutations = {
  SET_AGENDA_RANGE(state, { key, payload }) {
    state.agendaRanges = { ...state.agendaRanges, [key]: { ...payload, fetchedAt: Date.now() } }
  },

  SET_AGENDA_INFLIGHT(state, { key, promise }) {
    if (promise) state.agendaInflight[key] = promise
    else delete state.agendaInflight[key]
  },

  SET_AGENDA_LOADING(state, v) {
    state.agendaLoading = v
  },

  /**
   * 캐시 무효화. 일정/업무를 수정한 뒤 호출한다.
   * 인자가 없으면 전체를 비운다.
   */
  INVALIDATE_AGENDA(state, { from, to } = {}) {
    state.agendaGen += 1

    const overlaps = (key) => {
      if (!from || !to) return true
      const [kFrom, kTo] = key.split('~')
      return !(kTo < from || kFrom > to)
    }

    /*
     * **진행 중인 요청도 함께 버린다.**
     *
     * 그 요청은 방금의 쓰기보다 **먼저** 나갔으므로 결과에 이 변경이 없다.
     * 남겨 두면 바로 뒤의 fetchAgenda 가 같은 key 로 그 promise 를 재사용해서,
     * 방금 옮긴 일정이 옛 날짜로 돌아온 화면을 받고 그걸 fetchedAt=now 로
     * 캐시에 박는다 — 최대 60초 동안 되돌아간 채로 고정된다.
     * (드래그로 두 건을 연달아 옮기면 실제로 재현된다)
     */
    for (const key of Object.keys(state.agendaInflight)) {
      if (overlaps(key)) delete state.agendaInflight[key]
    }

    if (!from || !to) {
      state.agendaRanges = {}
      return
    }
    const next = {}
    for (const [key, val] of Object.entries(state.agendaRanges)) {
      // 겹치지 않는 범위만 남긴다
      if (!overlaps(key)) next[key] = val
    }
    state.agendaRanges = next
  }
}

export const actions = {
  /**
   * 범위 조회. 60초 이내면 캐시, 진행 중이면 같은 Promise 를 돌려준다
   * (여러 컴포넌트가 동시에 마운트돼도 요청은 하나).
   */
  async fetchAgenda({ state, commit }, params = {}) {
    const key = rangeKey(params)

    const hit = state.agendaRanges[key]
    if (hit && Date.now() - hit.fetchedAt < TTL_MS) return hit

    if (state.agendaInflight[key]) return state.agendaInflight[key]

    const query = {
      from: params.from,
      to: params.to
    }
    if (params.scope) query.scope = params.scope
    if (params.userId) query.userId = params.userId
    if (params.departmentId != null) query.departmentId = params.departmentId
    if (params.kinds && params.kinds.length) query.kinds = params.kinds.join(',')
    if (params.status) query.status = params.status
    if (params.assigneeId != null) query.assigneeId = params.assigneeId
    if (params.q) query.q = params.q
    if (params.eventType) query.eventType = params.eventType
    if (params.includeUnscheduled) query.includeUnscheduled = '1'

    commit('SET_AGENDA_LOADING', true)

    const gen = state.agendaGen

    const promise = http
      .get('/api/agenda', { params: query })
      .then((res) => {
        const payload = {
          items: normalizeAgendaList(res.data.items),
          unscheduled: normalizeAgendaList(res.data.unscheduled),
          counts: res.data.counts || {},
          truncated: res.data.truncated || {}
        }
        // 요청이 나간 뒤 무효화가 있었으면 이 응답은 이미 낡았다.
        // 호출자에게 돌려주기는 하되 캐시에는 넣지 않는다 — 넣으면 낡은
        // 화면이 TTL 60초 동안 고정된다.
        if (state.agendaGen === gen) commit('SET_AGENDA_RANGE', { key, payload })
        return payload
      })
      .finally(() => {
        // 무효화가 이미 이 key 를 지우고 새 요청이 등록됐을 수 있다.
        // 내 promise 일 때만 지운다 — 아니면 남의 인플라이트를 날린다.
        if (state.agendaInflight[key] === promise) {
          commit('SET_AGENDA_INFLIGHT', { key, promise: null })
        }
        commit('SET_AGENDA_LOADING', false)
      })

    commit('SET_AGENDA_INFLIGHT', { key, promise })
    return promise
  },

  /** 일정/업무 변경 후 호출 */
  invalidateAgenda({ commit }, range) {
    commit('INVALIDATE_AGENDA', range || {})
  }
}

export const getters = {
  agendaLoading: (state) => state.agendaLoading
}
